import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import Nav from "@/components/Nav";
import { Check, Sparkles, PawPrint, Camera, Heart, Zap } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: PawPrint, text: "Unlimited pet profiles" },
  { icon: Camera, text: "Unlimited photo uploads" },
  { icon: Heart, text: "Priority AI breed detection" },
  { icon: Zap, text: "Ad-free shareable scrapbooks" },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "petsnap_pro_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't start checkout");
      setBusy(false);
    }
  };

  const isPro = sub?.plan === "pro";

  return (
    <div className="min-h-screen">
      <Nav onUpload={() => navigate("/gallery")} />
      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-14 relative z-10">
        <div className="text-center mb-10">
          <div className="font-hand text-2xl text-[#E76F51] mb-1">unlock everything</div>
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-[#2D232E] tracking-tight leading-none">
            Simple, warm pricing.
          </h1>
          <p className="text-[#6A606C] mt-3 max-w-md mx-auto">
            One friendly plan. Cancel anytime. Your memories stay yours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-white border border-[#EAE2D6] p-8 warm-shadow">
            <div className="font-hand text-xl text-[#6A606C] mb-1">the starter</div>
            <h2 className="font-display text-3xl font-bold text-[#2D232E]">Free</h2>
            <div className="mt-4 mb-6">
              <span className="font-display text-5xl font-bold text-[#2D232E]">$0</span>
              <span className="text-[#6A606C] ml-1">/ forever</span>
            </div>
            <ul className="space-y-2.5 mb-6">
              <li className="flex items-center gap-2 text-[#2D232E]">
                <Check className="w-4 h-4 text-[#2A9D8F]" /> 1 pet profile
              </li>
              <li className="flex items-center gap-2 text-[#2D232E]">
                <Check className="w-4 h-4 text-[#2A9D8F]" /> Up to 20 photos
              </li>
              <li className="flex items-center gap-2 text-[#2D232E]">
                <Check className="w-4 h-4 text-[#2A9D8F]" /> AI breed detection
              </li>
              <li className="flex items-center gap-2 text-[#2D232E]">
                <Check className="w-4 h-4 text-[#2A9D8F]" /> Shareable scrapbooks
              </li>
            </ul>
            {!isPro && sub && (
              <div className="text-xs text-[#6A606C] bg-[#F4EFE6] rounded-xl p-3">
                You&apos;re on Free — {sub.pet_count}/{sub.pet_limit} pets, {sub.photo_count}/{sub.photo_limit} photos used.
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-[#2D232E] text-white p-8 warm-shadow relative overflow-hidden">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#E76F51]/40 blur-2xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-[#E9C46A] text-[#2D232E] font-hand text-lg px-3 py-0.5 rounded-full mb-1">
                <Sparkles className="w-4 h-4" /> most loved
              </div>
              <h2 className="font-display text-3xl font-bold">Petsnap Pro</h2>
              <div className="mt-4 mb-6">
                <span className="font-display text-5xl font-bold">$4.99</span>
                <span className="text-white/70 ml-1">/ month</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {FEATURES.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[#E9C46A]" /> {text}
                  </li>
                ))}
              </ul>
              {isPro ? (
                <div data-testid="pro-active-badge" className="rounded-2xl bg-white/10 border border-white/15 p-4 text-center">
                  <Sparkles className="w-5 h-5 text-[#E9C46A] mx-auto mb-1" />
                  <div className="font-display text-xl">You&apos;re a Pro member ✨</div>
                  <div className="text-sm text-white/70">Thanks for supporting Petsnap!</div>
                </div>
              ) : (
                <button
                  data-testid="upgrade-btn"
                  onClick={upgrade}
                  disabled={busy}
                  className="w-full rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold py-3 shadow-[0_8px_20px_rgba(231,111,81,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-60"
                >
                  {busy ? "Opening checkout..." : "Upgrade to Pro"}
                </button>
              )}
              <p className="text-xs text-white/60 mt-3 text-center">Test mode · use card 4242 4242 4242 4242</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
